import { useState, useEffect } from "react";
import { surchargeItems, cannotTakeItems, bookingServices } from "../../../mocks/bookingData";
import type { BookingItemData } from "../../../mocks/bookingData";
import type { BookingState } from "../page";
import OrderSummary from "./OrderSummary";
import BookingCalendar from "./BookingCalendar";
import ItemsFilterList from "./ItemsFilterList";

type PaymentMethod = "cash" | "card" | null;

interface Step3CombinedProps {
  booking: BookingState;
  onUpdate: (updates: Partial<BookingState>) => void;
  onBack: () => void;
  onSubmit: () => void;           // cash → WC order + email + success screen
  onStripeCheckout: () => void;   // card  → WC pending order + redirect to Stripe
  total: number;
  submitting?: boolean;
  bookCTA?: string;
  totalLabel?: string;
}

const MOCK_POSTCODES_COVERED = ["WR", "B", "CV", "GL", "HR", "DY", "WS", "ST", "TF", "SY", "WV", "NN", "MK", "OX", "RG", "SN", "SP", "BA", "BS", "TA", "EX", "PL", "TQ", "TR", "TN", "ME", "DA", "BR", "SE", "SW", "W", "E", "N", "NW", "EC", "WC", "HA", "UB", "SL", "HP", "LU", "AL", "SG", "CM", "SS", "CO", "IP", "NR", "PE", "LE", "DE", "NG", "LN", "HU", "YO", "LS", "BD", "HX", "HD", "WF", "DN", "S", "SK", "M", "OL", "BL", "WN", "PR", "BB", "FY", "LA", "CA", "DL", "TS", "SR", "NE", "DH", "G"];

function getPostcodePrefix(p: string) {
  return p.trim().toUpperCase().replace(/[^A-Z]/g, "").substring(0, 2);
}

export default function Step3Combined({
  booking,
  onUpdate,
  onBack,
  onSubmit,
  onStripeCheckout,
  total,
  submitting = false,
  bookCTA = "Confirm Booking",
}: Step3CombinedProps) {
  const [postcodeInput, setPostcodeInput]           = useState(booking.postcode);
  const [postcodeChecked, setPostcodeChecked]       = useState(false);
  const [postcodeAvailable, setPostcodeAvailable]   = useState<boolean | null>(null);
  const [checkingPostcode, setCheckingPostcode]     = useState(false);
  const [agreed, setAgreed]                         = useState(false);
  const [localSubmitting, setLocalSubmitting]       = useState(false);
  const [errors, setErrors]                         = useState<Record<string, string>>({});
  const [itemsExpanded, setItemsExpanded]           = useState(false);
  const [photos, setPhotos]                         = useState<{ file: File; preview: string }[]>([]);
  const [noProhibited, setNoProhibited]             = useState(false);
  const [paymentMethod, setPaymentMethod]           = useState<PaymentMethod>(null);

  const isLoadBased  = booking.stepType === "load";
  const isItemsBased = booking.stepType === "items" || booking.stepType === "bin";
  const isBin        = booking.stepType === "bin";

  const serviceConfig = bookingServices.find((s) => s.slug === booking.serviceSlug);
  const itemsConfig   = serviceConfig?.itemsConfig;
  const cartTotal     = booking.cartItems.reduce((s, c) => s + c.quantity * c.unitPrice, 0);

  const isSubmitting = submitting || localSubmitting;

  const updateItemQty = (item: BookingItemData, delta: number) => {
    const existing = booking.cartItems.find((c) => c.id === item.id);
    if (!existing && delta > 0) {
      onUpdate({ cartItems: [...booking.cartItems, { id: item.id, label: item.label, quantity: 1, unitPrice: item.unitPrice }] });
    } else if (existing) {
      const newQty = existing.quantity + delta;
      if (newQty <= 0) onUpdate({ cartItems: booking.cartItems.filter((c) => c.id !== item.id) });
      else onUpdate({ cartItems: booking.cartItems.map((c) => c.id === item.id ? { ...c, quantity: newQty } : c) });
    }
  };

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const newPhotos = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, 8 - photos.length)
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  useEffect(() => {
    if (booking.postcode && !postcodeChecked) {
      setCheckingPostcode(true);
      setTimeout(() => {
        const prefix = getPostcodePrefix(booking.postcode);
        const ok = MOCK_POSTCODES_COVERED.some((p) => prefix.startsWith(p) || p.startsWith(prefix.substring(0, 1)));
        setPostcodeAvailable(ok);
        setPostcodeChecked(true);
        setCheckingPostcode(false);
      }, 600);
    }
  }, []);

  const checkPostcode = () => {
    if (!postcodeInput.trim()) { setErrors((e) => ({ ...e, postcode: "Please enter a postcode" })); return; }
    setCheckingPostcode(true);
    setTimeout(() => {
      const prefix = getPostcodePrefix(postcodeInput);
      const ok = MOCK_POSTCODES_COVERED.some((p) => prefix.startsWith(p) || p.startsWith(prefix.substring(0, 1)));
      setPostcodeAvailable(ok);
      setPostcodeChecked(true);
      setCheckingPostcode(false);
      if (ok) onUpdate({ postcode: postcodeInput.trim().toUpperCase() });
    }, 800);
  };

  const updateSurcharge = (item: { id: string; label: string; unitPrice: number }, delta: number) => {
    const existing = booking.cartItems.find((c) => c.id === item.id);
    if (!existing && delta > 0) {
      onUpdate({ cartItems: [...booking.cartItems, { id: item.id, label: item.label, quantity: 1, unitPrice: item.unitPrice }] });
    } else if (existing) {
      const newQty = existing.quantity + delta;
      if (newQty <= 0) onUpdate({ cartItems: booking.cartItems.filter((c) => c.id !== item.id) });
      else onUpdate({ cartItems: booking.cartItems.map((c) => c.id === item.id ? { ...c, quantity: newQty } : c) });
    }
  };
  const getSurchargeQty = (id: string) => booking.cartItems.find((c) => c.id === id)?.quantity ?? 0;

  const clearError = (key: string) => {
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleBook = () => {
    const e: Record<string, string> = {};
    if (!booking.date)                                   e.date          = "Please select a collection date";
    if (!booking.timeSlot)                               e.timeSlot      = "Please select a time slot";
    if (!booking.houseNumber?.trim())                    e.houseNumber   = "House number is required";
    if (!booking.addressLine1.trim())                    e.addressLine1  = "Street name is required";
    if (!booking.city.trim())                            e.city          = "Town / city is required";
    if (!booking.postcode || postcodeAvailable !== true) e.postcode      = "Please enter and check your postcode";
    if (!booking.firstName.trim())                       e.firstName     = "First name is required";
    if (!booking.lastName.trim())                        e.lastName      = "Last name is required";
    if (!booking.email.trim())                           e.email         = "Email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(booking.email)) e.email = "Please enter a valid email";
    if (!booking.phone.trim())                           e.phone         = "Phone number is required";
    if (!agreed)                                         e.agreed        = "Please accept the terms to continue";
    if (isBin && !noProhibited)                          e.noProhibited  = "Please confirm your items contain no prohibited waste";
    if (!paymentMethod)                                  e.paymentMethod = "Please select how you'd like to pay";
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});

    if (paymentMethod === "card") {
      onStripeCheckout();
      return;
    }

    // Cash — brief spinner then hand off
    setLocalSubmitting(true);
    setTimeout(() => { setLocalSubmitting(false); onSubmit(); }, 1200);
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-4 thick-border cartoon-shadow-sm">
          <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Step 3 of 3</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-black text-cl-dark mb-3">Complete your booking</h2>
        <p className="text-cl-dark/60 font-semibold">Date, location, and your details — all in one place.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0 flex flex-col gap-6">

          {/* ── Items editor ── */}
          {isItemsBased && itemsConfig && (
            <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
              <button onClick={() => setItemsExpanded((v) => !v)}
                className="w-full px-6 py-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-cl-tint/40 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green flex-shrink-0">
                    <i className="ri-list-check text-sm"></i>
                  </span>
                  <div className="text-left">
                    <h3 className="font-black text-cl-dark text-base">Your Items</h3>
                    <p className="text-xs text-gray-500 font-semibold">
                      {booking.cartItems.length === 0
                        ? "No items selected — add items below"
                        : `${booking.cartItems.reduce((s, c) => s + c.quantity, 0)} item(s) · £${cartTotal.toFixed(2)} — click to edit`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {booking.cartItems.length > 0 && (
                    <span className="bg-cl-lime border-2 border-cl-green text-cl-dark text-xs font-black px-3 py-1 rounded-full">
                      £{cartTotal.toFixed(2)}
                    </span>
                  )}
                  <i className={`ri-arrow-${itemsExpanded ? "up" : "down"}-s-line text-cl-dark text-lg`}></i>
                </div>
              </button>
              {itemsExpanded && (
                <div className="border-t-2 border-transparent p-5">
                  <p className="text-xs text-gray-500 font-semibold mb-4">Forgot something? Add or remove items below — your total will update automatically.</p>
                  <ItemsFilterList items={itemsConfig} cartItems={booking.cartItems} onQtyChange={updateItemQty} />
                </div>
              )}
            </div>
          )}

          {/* ── Date & Time ── */}
          <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
            <div className="px-6 py-4 border-b-2 border-transparent flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green">
                <i className="ri-calendar-line text-sm"></i>
              </span>
              <h3 className="font-black text-cl-dark text-base">Date &amp; Time</h3>
            </div>
            <div className="p-5">
              <BookingCalendar
                booking={booking}
                onUpdate={(u) => { onUpdate(u); if (u.date) clearError("date"); if (u.timeSlot) clearError("timeSlot"); }}
                error={errors.date || errors.timeSlot}
                photos={photos}
                onAddPhotos={addPhotos}
                onRemovePhoto={removePhoto}
              />
            </div>
          </div>

          {/* ── Surcharges (load) ── */}
          {isLoadBased && (
            <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-transparent flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-orange-50 border-2 border-orange-200 text-orange-500">
                  <i className="ri-add-circle-line text-sm"></i>
                </span>
                <div>
                  <h3 className="font-black text-cl-dark text-base">Surcharges</h3>
                  <p className="text-xs text-gray-500 font-semibold">Specialist items needing extra disposal — add any that apply.</p>
                </div>
              </div>
              <div className="divide-y divide-transparent">
                {surchargeItems.map((item) => {
                  const qty = getSurchargeQty(item.id);
                  return (
                    <div key={item.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-orange-50/30 transition-colors">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 border border-orange-200 text-orange-500 flex-shrink-0">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-black text-cl-dark">{item.label}</p>
                        <p className="text-xs text-orange-500 font-bold">+£{item.unitPrice.toFixed(2)} each</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => updateSurcharge(item, -1)} disabled={qty === 0}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg border-2 font-black text-sm transition-all cursor-pointer ${qty === 0 ? "border-transparent text-gray-300" : "border-cl-dark text-cl-dark hover:bg-cl-dark hover:text-white"}`}>
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className={`w-7 text-center font-black text-sm ${qty > 0 ? "text-orange-500" : "text-gray-400"}`}>{qty}</span>
                        <button onClick={() => updateSurcharge(item, 1)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-cl-dark text-cl-dark hover:bg-cl-dark hover:text-white font-black text-sm transition-all cursor-pointer">
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mx-5 mb-5 mt-3 rounded-2xl overflow-hidden" style={{ background: "#140404" }}>
                <div className="bg-red-600 px-5 py-4 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 flex-shrink-0">
                    <i className="ri-error-warning-fill text-white text-xl"></i>
                  </span>
                  <div>
                    <p className="text-base font-black text-white uppercase tracking-wide">Items We Cannot Collect</p>
                    <p className="text-red-100 text-xs font-semibold">Hazardous waste — we are not licensed to carry these</p>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {cannotTakeItems.map((item) => (
                      <div key={item} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,0.06)" }}>
                        <i className="ri-close-circle-fill text-red-400 text-base flex-shrink-0"></i>
                        <span className="text-sm font-bold text-white/90">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                    <p className="text-sm text-white/60 font-semibold flex-1">Call us for chest freezers, AC units, or pianos.</p>
                    <a href="tel:03335775445" className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black text-sm px-5 py-2.5 rounded-xl whitespace-nowrap transition-colors cursor-pointer">
                      <i className="ri-phone-fill"></i>03335 775445
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Cannot collect (bin) ── */}
          {isBin && (
            <div className="rounded-2xl overflow-hidden" style={{ background: "#140404" }}>
              <div className="bg-red-600 px-5 py-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 flex-shrink-0">
                  <i className="ri-error-warning-fill text-white text-xl"></i>
                </span>
                <div>
                  <p className="text-base font-black text-white uppercase tracking-wide">Items We Cannot Collect</p>
                  <p className="text-red-100 text-xs font-semibold">Hazardous waste — we are not licensed to carry these</p>
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  {cannotTakeItems.map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <i className="ri-close-circle-fill text-red-400 text-base flex-shrink-0"></i>
                      <span className="text-sm font-bold text-white/90">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  <p className="text-sm text-white/60 font-semibold flex-1">Call us for chest freezers, AC units, or pianos.</p>
                  <a href="tel:03335775445" className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black text-sm px-5 py-2.5 rounded-xl whitespace-nowrap transition-colors cursor-pointer">
                    <i className="ri-phone-fill"></i>03335 775445
                  </a>
                </div>
                <div className={`rounded-xl p-4 border-2 transition-colors ${errors.noProhibited ? "border-red-400 bg-red-900/30" : "bg-white/5 border-white/10"}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div onClick={() => { setNoProhibited(!noProhibited); clearError("noProhibited"); }}
                      className={`w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 mt-0.5 transition-all cursor-pointer ${noProhibited ? "bg-cl-green border-[#4a8c0a]" : "bg-white/10 border-white/30"}`}>
                      {noProhibited && <i className="ri-check-line text-white text-xs font-black"></i>}
                    </div>
                    <p className="text-sm text-white/80 font-semibold leading-relaxed">I confirm my order does not contain any prohibited items</p>
                  </label>
                  {errors.noProhibited && <p className="text-xs text-red-400 mt-2 font-bold flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.noProhibited}</p>}
                </div>
              </div>
            </div>
          )}

          {/* ── Your Details ── */}
          <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
            <div className="px-6 py-4 border-b-2 border-transparent flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-green text-cl-green">
                <i className="ri-user-3-line text-sm"></i>
              </span>
              <h3 className="font-black text-cl-dark text-base">Your Details</h3>
            </div>
            <div className="p-5 flex flex-col gap-4">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">First Name <span className="text-cl-green">*</span></label>
                  <input type="text" value={booking.firstName}
                    onChange={(e) => { onUpdate({ firstName: e.target.value }); clearError("firstName"); }}
                    placeholder="John"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.firstName ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.firstName && <p className="text-xs text-red-500 mt-1 font-bold">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">Last Name <span className="text-cl-green">*</span></label>
                  <input type="text" value={booking.lastName}
                    onChange={(e) => { onUpdate({ lastName: e.target.value }); clearError("lastName"); }}
                    placeholder="Smith"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.lastName ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.lastName && <p className="text-xs text-red-500 mt-1 font-bold">{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">Email Address <span className="text-cl-green">*</span></label>
                  <input type="email" value={booking.email}
                    onChange={(e) => { onUpdate({ email: e.target.value }); clearError("email"); }}
                    placeholder="john@example.com"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.email ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1 font-bold">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">Phone Number <span className="text-cl-green">*</span></label>
                  <input type="tel" value={booking.phone}
                    onChange={(e) => { onUpdate({ phone: e.target.value }); clearError("phone"); }}
                    placeholder="07700 900000"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.phone ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.phone && <p className="text-xs text-red-500 mt-1 font-bold">{errors.phone}</p>}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="flex items-center gap-1.5 text-xs font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                  <i className="ri-map-pin-line text-cl-green"></i>Collection Address
                </span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-1">
                  <label className="block text-sm font-black text-cl-dark mb-2">House No. <span className="text-cl-green">*</span></label>
                  <input type="text" value={booking.houseNumber}
                    onChange={(e) => { onUpdate({ houseNumber: e.target.value }); clearError("houseNumber"); }}
                    placeholder="10"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.houseNumber ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.houseNumber && <p className="text-xs text-red-500 mt-1 font-bold">{errors.houseNumber}</p>}
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-sm font-black text-cl-dark mb-2">Street <span className="text-cl-green">*</span></label>
                  <input type="text" value={booking.addressLine1}
                    onChange={(e) => { onUpdate({ addressLine1: e.target.value }); clearError("addressLine1"); }}
                    placeholder="e.g. High Street"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.addressLine1 ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.addressLine1 && <p className="text-xs text-red-500 mt-1 font-bold">{errors.addressLine1}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-cl-dark mb-2">Address Line 2 <span className="text-gray-400 font-semibold text-xs">(optional)</span></label>
                <input type="text" value={booking.addressLine2}
                  onChange={(e) => onUpdate({ addressLine2: e.target.value })}
                  placeholder="Flat, unit, or building name"
                  className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none focus:border-cl-dark transition-colors"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">Town / City <span className="text-cl-green">*</span></label>
                  <input type="text" value={booking.city}
                    onChange={(e) => { onUpdate({ city: e.target.value }); clearError("city"); }}
                    placeholder="e.g. Droitwich"
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.city ? "border-red-400 bg-red-50" : "border-transparent focus:border-cl-dark"}`}
                  />
                  {errors.city && <p className="text-xs text-red-500 mt-1 font-bold">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">Postcode <span className="text-cl-green">*</span></label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      {postcodeAvailable === true && <i className="ri-checkbox-circle-fill absolute right-3 top-1/2 -translate-y-1/2 text-cl-green text-base z-10"></i>}
                      {checkingPostcode && <i className="ri-loader-4-line animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-cl-green text-base z-10"></i>}
                      <input type="text" value={postcodeInput}
                        onChange={(e) => { setPostcodeInput(e.target.value); setPostcodeChecked(false); setPostcodeAvailable(null); onUpdate({ postcode: e.target.value }); clearError("postcode"); }}
                        onKeyDown={(e) => e.key === "Enter" && checkPostcode()}
                        placeholder="e.g. WR9 0BX"
                        className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none transition-colors ${errors.postcode || postcodeAvailable === false ? "border-red-400 bg-red-50" : postcodeAvailable === true ? "border-cl-green bg-[#f0fce4]" : "border-transparent focus:border-cl-dark"}`}
                      />
                    </div>
                    <button onClick={checkPostcode} disabled={checkingPostcode}
                      className="bg-cl-dark text-white font-black px-4 py-3 rounded-xl text-sm cartoon-btn whitespace-nowrap cursor-pointer flex-shrink-0">
                      {checkingPostcode ? <i className="ri-loader-4-line animate-spin"></i> : "Check"}
                    </button>
                  </div>
                  {postcodeAvailable === false && <p className="text-xs text-red-500 mt-1.5 font-bold flex items-center gap-1"><i className="ri-close-circle-fill"></i>We don&apos;t cover this postcode yet.</p>}
                  {postcodeAvailable === true && <p className="text-xs text-cl-green mt-1 font-bold flex items-center gap-1"><i className="ri-checkbox-circle-line"></i>Coverage confirmed</p>}
                  {errors.postcode && <p className="text-xs text-red-500 mt-1 font-bold">{errors.postcode}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-cl-dark mb-2">County <span className="text-gray-400 font-semibold text-xs">(optional)</span></label>
                <input type="text" value={booking.county}
                  onChange={(e) => onUpdate({ county: e.target.value })}
                  placeholder="e.g. Worcestershire"
                  className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none focus:border-cl-dark transition-colors"
                />
              </div>

              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="flex items-center gap-1.5 text-xs font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                  <i className="ri-chat-3-line text-cl-green"></i>Extra Info
                </span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              <div>
                <label className="block text-sm font-black text-cl-dark mb-2">Additional Instructions <span className="text-gray-400 font-semibold text-xs">(optional)</span></label>
                <textarea value={booking.notes} onChange={(e) => onUpdate({ notes: e.target.value })}
                  placeholder="Gate codes, parking info, items to leave behind..."
                  rows={3} maxLength={500}
                  className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none focus:border-cl-dark transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{booking.notes.length}/500</p>
              </div>

              {isBin && (
                <div>
                  <label className="block text-sm font-black text-cl-dark mb-2">Waste Description <span className="text-gray-400 font-semibold text-xs">(optional)</span></label>
                  <textarea value={booking.wasteDescription} onChange={(e) => onUpdate({ wasteDescription: e.target.value })}
                    placeholder="Describe the waste you need collected e.g. household rubbish, garden waste, old furniture..."
                    rows={3} maxLength={500}
                    className="w-full border-2 border-transparent rounded-xl px-4 py-3 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none focus:border-cl-dark transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{booking.wasteDescription.length}/500</p>
                </div>
              )}

              <div className={`rounded-xl p-4 border-2 transition-colors ${errors.agreed ? "border-red-300 bg-red-50" : "border-transparent bg-cl-tint"}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <div onClick={() => { setAgreed(!agreed); clearError("agreed"); }}
                    className={`w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 mt-0.5 transition-all cursor-pointer ${agreed ? "bg-cl-green border-[#4a8c0a]" : "bg-white border-transparent"}`}>
                    {agreed && <i className="ri-check-line text-white text-xs font-black"></i>}
                  </div>
                  <p className="text-sm text-gray-600 font-semibold leading-relaxed">
                    I agree to Cleario&apos;s{" "}
                    <a href="#" className="text-cl-green font-black hover:underline">Terms &amp; Conditions</a>{" "}
                    and{" "}
                    <a href="#" className="text-cl-green font-black hover:underline">Privacy Policy</a>.
                    I confirm the waste I am removing is non-hazardous.
                  </p>
                </label>
                {errors.agreed && <p className="text-xs text-red-500 mt-2 font-bold flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.agreed}</p>}
              </div>
            </div>
          </div>

          <div className="flex lg:hidden">
            <button onClick={onBack} className="flex items-center gap-2 text-sm font-black text-cl-dark/60 hover:text-cl-dark transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i> Back
            </button>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="lg:w-80 xl:w-96 flex-shrink-0 flex flex-col gap-4">
          <div className="sticky top-24">
            <OrderSummary booking={booking} total={total} />

            {/* ── Payment Method ── */}
            <div className="mt-4 bg-cl-dark rounded-2xl cartoon-card overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-lime border-2 border-cl-green text-cl-green">
                  <i className="ri-bank-card-line text-xs"></i>
                </span>
                <h3 className="font-black text-white text-sm">How would you like to pay?</h3>
              </div>
              <div className="p-3 flex flex-col gap-2">

                {/* Cash */}
                <button
                  onClick={() => { setPaymentMethod("cash"); clearError("paymentMethod"); }}
                  className={`w-full flex items-center gap-3 rounded-xl border-2 px-3 py-3 transition-all cursor-pointer text-left ${
                    paymentMethod === "cash" ? "border-cl-lime/70 bg-white/10"
                    : errors.paymentMethod ? "border-red-400/50 bg-red-500/10"
                    : "border-white/10 bg-white/5 hover:border-cl-lime/40"
                  }`}
                >
                  <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${paymentMethod === "cash" ? "bg-cl-lime text-cl-dark" : "bg-white/10 text-cl-lime"}`}>
                    <i className="ri-money-pound-circle-line text-base"></i>
                  </span>
                  <div className="flex-1">
                    <p className="font-black text-white text-xs">Cash</p>
                    <p className="text-[10px] text-white/50 font-semibold mt-0.5">Book now, pay when we collect.</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${paymentMethod === "cash" ? "border-cl-lime bg-cl-lime" : "border-white/30"}`}>
                    {paymentMethod === "cash" && <div className="w-1.5 h-1.5 rounded-full bg-cl-dark"></div>}
                  </div>
                </button>

                {/* Card */}
                <button
                  onClick={() => { setPaymentMethod("card"); clearError("paymentMethod"); }}
                  className={`w-full flex items-center gap-3 rounded-xl border-2 px-3 py-3 transition-all cursor-pointer text-left ${
                    paymentMethod === "card" ? "border-cl-lime/70 bg-white/10"
                    : errors.paymentMethod ? "border-red-400/50 bg-red-500/10"
                    : "border-white/10 bg-white/5 hover:border-cl-lime/40"
                  }`}
                >
                  <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${paymentMethod === "card" ? "bg-cl-lime text-cl-dark" : "bg-white/10 text-cl-lime"}`}>
                    <i className="ri-visa-line text-base"></i>
                  </span>
                  <div className="flex-1">
                    <p className="font-black text-white text-xs">Card</p>
                    <p className="text-[10px] text-white/50 font-semibold mt-0.5">Secure Stripe checkout.</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${paymentMethod === "card" ? "border-cl-lime bg-cl-lime" : "border-white/30"}`}>
                    {paymentMethod === "card" && <div className="w-1.5 h-1.5 rounded-full bg-cl-dark"></div>}
                  </div>
                </button>

                {errors.paymentMethod && (
                  <p className="text-[10px] text-red-400 font-bold flex items-center gap-1 px-1">
                    <i className="ri-error-warning-line"></i>{errors.paymentMethod}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleBook}
              disabled={isSubmitting || !paymentMethod}
              className={`w-full mt-3 font-black py-4 rounded-xl text-base disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer transition-all ${
                paymentMethod === "card" ? "bg-cl-dark text-white cartoon-btn" : "bg-cl-green text-white cartoon-btn-green"
              }`}
            >
              {isSubmitting ? (
                <><i className="ri-loader-4-line animate-spin text-lg"></i>
                  {paymentMethod === "card" ? "Redirecting to payment..." : "Confirming booking..."}
                </>
              ) : paymentMethod === "card" ? (
                <><i className="ri-lock-line text-lg"></i>Card &nbsp;·&nbsp; £{total.toFixed(2)}</>
              ) : (
                <><i className="ri-shopping-basket-line text-lg"></i>
                  {paymentMethod === "cash" ? `Confirm Booking · £${total.toFixed(2)}` : `${bookCTA} · £${total.toFixed(2)}`}
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center font-semibold mt-3">
              {paymentMethod === "card"
                ? "You'll be redirected to Stripe's secure checkout page."
                : "You won't be charged until after collection. We'll call to confirm your slot."}
            </p>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {[
                { icon: "ri-shield-check-line", text: "Fully licensed" },
                { icon: "ri-lock-line",         text: "Secure booking" },
                { icon: "ri-leaf-line",         text: ">95% recycled" },
                { icon: "ri-file-text-line",    text: "Transfer note" },
              ].map((item) => (
                <div key={item.text} className="bg-white rounded-xl p-2.5 cartoon-card flex flex-col items-center gap-1 text-center">
                  <i className={`${item.icon} text-cl-green text-base`}></i>
                  <p className="text-xs font-bold text-cl-dark">{item.text}</p>
                </div>
              ))}
            </div>

            <button onClick={onBack} className="hidden lg:flex items-center gap-2 text-sm font-black text-cl-dark/60 hover:text-cl-dark transition-colors cursor-pointer mt-4">
              <i className="ri-arrow-left-line"></i> Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}